// ─── PRODUCTS ──────────────────────────────────────────────────
const resizeProductImage = (file) => new Promise((resolve, reject) => {
  if (!file?.type?.startsWith('image/')) { reject(new Error('Choose an image file')); return; }
  const reader = new FileReader();
  reader.onload = () => {
    const img = new Image();
    img.onload = () => {
      const size = 256;
      const scale = Math.min(1, size / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, w, h);
      const webp = canvas.toDataURL('image/webp', 0.78);
      resolve(webp.startsWith('data:image/webp') ? webp : canvas.toDataURL('image/jpeg', 0.82));
    };
    img.onerror = () => reject(new Error('Could not read image'));
    img.src = reader.result;
  };
  reader.onerror = () => reject(new Error('Could not read image'));
  reader.readAsDataURL(file);
});

const ProductThumb = ({ src, size = 38 }) => (
  <div style={{ width:size, height:size, borderRadius:8, background: src ? 'transparent' : '#F3F4F6', border: src ? 'none' : '1px solid #E5E7EB', overflow:'hidden', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
    {src
      ? <img src={src} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
      : <Package size={Math.max(15, Math.round(size * 0.45))} strokeWidth={1.7} color="#9CA3AF"/>
    }
  </div>
);

const ProductForm = ({ product, rawName, onSaved, onCancel }) => {
  const [name, setName] = useState(product?.name || rawName || '');
  const [sku, setSku] = useState(product?.sku || '');
  const [imageData, setImageData] = useState(product?.image_data || '');
  const [trackStock, setTrackStock] = useState(!!product?.track_stock);
  const [stockQty, setStockQty] = useState(product?.stock_qty || 0);
  const [lowStockThreshold, setLowStockThreshold] = useState(product?.low_stock_threshold ?? 5);
  const [customsDescription, setCustomsDescription] = useState(product?.customs_description || '');
  const [hsCode, setHsCode] = useState(product?.hs_code || '');
  const [countryOfOrigin, setCountryOfOrigin] = useState(product?.country_of_origin || '');
  const [netWeightKg, setNetWeightKg] = useState(product?.net_weight_kg ?? '');
  const [grossWeightKg, setGrossWeightKg] = useState(product?.gross_weight_kg ?? '');
  const [sendConfirmationEmail, setSendConfirmationEmail] = useState(!!product?.send_confirmation_email);
  const [emailTemplateId, setEmailTemplateId] = useState(product?.email_template_id ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const handleImage = async file => {
    setError('');
    try {
      setImageData(await resizeProductImage(file));
    } catch (err) {
      setError(err.message || 'Could not upload image');
    }
  };

  const save = async e => {
    e.preventDefault();
    setError('');
    if (netWeightKg !== '' && grossWeightKg !== '' && Number(grossWeightKg) < Number(netWeightKg)) {
      setError('Unit gross weight must be at least the unit net weight.');
      return;
    }
    setSaving(true);
    const body = {
      name: name.trim(),
      sku: sku.trim(),
      image_data: imageData || null,
      track_stock: trackStock,
      stock_qty: Number(stockQty) || 0,
      low_stock_threshold: Math.max(0, Number.parseInt(lowStockThreshold, 10) || 0),
      customs_description: customsDescription.trim(),
      hs_code: hsCode.trim(),
      country_of_origin: countryOfOrigin.trim().toUpperCase(),
      net_weight_kg: netWeightKg === '' ? null : Number(netWeightKg),
      gross_weight_kg: grossWeightKg === '' ? null : Number(grossWeightKg),
      send_confirmation_email: sendConfirmationEmail,
      email_template_id: emailTemplateId === '' ? null : Number(emailTemplateId),
      aliases: rawName ? [{ raw_name: rawName }] : [],
    };
    const path = product ? `/api/products/${product.id}` : '/api/products';
    const { ok, data } = await api(product ? 'PATCH' : 'POST', path, body);
    setSaving(false);
    if (!ok) { setError(data.error || 'Could not save product'); return; }
    onSaved?.(data.product);
  };

  const input = { width:'100%', border:'1px solid #E5E7EB', borderRadius:8, padding:'9px 11px', fontSize:13, outline:'none', background:'#fff' };
  const label = { fontSize:11, fontWeight:700, color:'#6B7280', marginBottom:6, display:'block' };

  return (
    <form onSubmit={save} style={{ display:'grid', gap:12 }}>
      <div>
        <label style={label}>PRODUCT NAME <span style={{ color:'#DC2626' }}>*</span></label>
        <input value={name} onChange={e => setName(e.target.value)} style={input} placeholder="e.g. Deluxe board game"/>
      </div>
      <div>
        <label style={label}>PRODUCT IMAGE <span style={{ color:'#9CA3AF', fontWeight:600 }}>(OPTIONAL, 256×256 MAX)</span></label>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <ProductThumb src={imageData} size={58}/>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={e => e.target.files?.[0] && handleImage(e.target.files[0])} style={{ display:'none' }}/>
            <button type="button" onClick={() => fileRef.current?.click()} style={{ background:'#fff', color:'#374151', border:'1px solid #E5E7EB', borderRadius:8, padding:'8px 12px', fontSize:13, fontWeight:600, cursor:'pointer' }}>Upload image</button>
            {imageData && <button type="button" onClick={() => setImageData('')} style={{ background:'#F9FAFB', color:'#6B7280', border:'1px solid #E5E7EB', borderRadius:8, padding:'8px 12px', fontSize:13, cursor:'pointer' }}>Remove</button>}
          </div>
        </div>
      </div>
      <div>
        <label style={label}>SKU <span style={{ color:'#9CA3AF', fontWeight:600 }}>(OPTIONAL)</span></label>
        <input value={sku} onChange={e => setSku(e.target.value)} style={input} placeholder="e.g. DGB-001"/>
      </div>
      <div style={{ border:'1px solid #E5E7EB', borderRadius:8, padding:12, display:'grid', gap:11, background:'#FAFAFA' }}>
        <div>
          <div style={{ fontSize:12, fontWeight:800, color:'#374151' }}>Customs information</div>
          <div style={{ fontSize:11, color:'#9CA3AF', marginTop:2, lineHeight:1.4 }}>Used on commercial invoices for shipments outside the EU.</div>
        </div>
        <div>
          <label style={label}>CUSTOMS DESCRIPTION IN ENGLISH</label>
          <input value={customsDescription} onChange={e => setCustomsDescription(e.target.value)} maxLength={240} style={input} placeholder="e.g. Cardboard board game with plastic pieces"/>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'minmax(0, 1fr) minmax(0, 1fr)', gap:10 }}>
          <div>
            <label style={label}>HS CODE</label>
            <input value={hsCode} onChange={e => setHsCode(e.target.value.replace(/\D/g, '').slice(0, 12))} inputMode="numeric" style={input} placeholder="e.g. 95049080"/>
          </div>
          <div>
            <label style={label}>COUNTRY OF ORIGIN</label>
            <input value={countryOfOrigin} onChange={e => setCountryOfOrigin(e.target.value.replace(/[^A-Za-z]/g, '').slice(0, 2).toUpperCase())} maxLength={2} style={{ ...input, textTransform:'uppercase' }} placeholder="e.g. CN"/>
          </div>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'minmax(0, 1fr) minmax(0, 1fr)', gap:10 }}>
          <div>
            <label style={label}>UNIT NET WEIGHT (KG)</label>
            <input type="number" min="0" step="0.001" value={netWeightKg} onChange={e => setNetWeightKg(e.target.value)} style={input} placeholder="e.g. 1.100"/>
          </div>
          <div>
            <label style={label}>UNIT GROSS WEIGHT (KG)</label>
            <input type="number" min="0" step="0.001" value={grossWeightKg} onChange={e => setGrossWeightKg(e.target.value)} style={input} placeholder="e.g. 1.250"/>
          </div>
        </div>
      </div>
      <label style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, border:'1px solid #E5E7EB', borderRadius:8, padding:'10px 12px', cursor:'pointer' }}>
        <span>
          <span style={{ display:'block', fontSize:13, fontWeight:600, color:'#374151' }}>Track inventory count</span>
          <span style={{ display:'block', fontSize:11, color:'#9CA3AF', marginTop:2 }}>Use the number below as stock on hand, then subtract assigned order quantities.</span>
        </span>
        <input type="checkbox" checked={trackStock} onChange={e => setTrackStock(e.target.checked)} style={{ width:16, height:16 }}/>
      </label>
      {trackStock && (
        <div style={{ display:'grid', gridTemplateColumns:'minmax(0, 1fr) minmax(0, 1fr)', gap:10 }}>
          <div>
            <label style={label}>UNITS IN STOCK</label>
            <input type="number" min="0" step="1" value={stockQty} onChange={e => setStockQty(e.target.value)} style={input} placeholder="How many units are available"/>
          </div>
          <div>
            <label style={label}>LOW STOCK AT</label>
            <input type="number" min="0" step="1" value={lowStockThreshold} onChange={e => setLowStockThreshold(e.target.value)} style={input} placeholder="e.g. 5"/>
            <div style={{ marginTop:5, fontSize:11, color:'#9CA3AF', lineHeight:1.35 }}>Flag low stock when remaining units are at or below this number.</div>
          </div>
        </div>
      )}
      <label style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, border:'1px solid #E5E7EB', borderRadius:8, padding:'10px 12px', cursor:'pointer' }}>
        <span>
          <span style={{ display:'block', fontSize:13, fontWeight:600, color:'#374151' }}>Send order confirmation email</span>
          <span style={{ display:'block', fontSize:11, color:'#9CA3AF', marginTop:2 }}>When an order includes this product, send a Brevo confirmation email. If an order has several email-enabled products, the highest-value one's template is used (unless "send one per SKU" is on in Settings).</span>
        </span>
        <input type="checkbox" checked={sendConfirmationEmail} onChange={e => setSendConfirmationEmail(e.target.checked)} style={{ width:16, height:16 }}/>
      </label>
      {sendConfirmationEmail && (
        <div>
          <label style={label}>BREVO TEMPLATE ID</label>
          <input type="number" min="1" step="1" value={emailTemplateId} onChange={e => setEmailTemplateId(e.target.value)} style={input} placeholder="e.g. 3"/>
          <div style={{ marginTop:5, fontSize:11, color:'#9CA3AF', lineHeight:1.35 }}>The transactional template ID from your Brevo account for this product.</div>
        </div>
      )}
      {error && <div style={{ color:'#DC2626', fontSize:12 }}>{error}</div>}
      <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
        {onCancel && <button type="button" onClick={onCancel} style={{ background:'#fff', color:'#374151', border:'1px solid #E5E7EB', borderRadius:8, padding:'8px 12px', fontSize:13, fontWeight:600, cursor:'pointer' }}>Cancel</button>}
        <button type="submit" disabled={saving} style={{ background: saving ? '#93C5FD' : '#006FDD', color:'#fff', border:'none', borderRadius:8, padding:'8px 13px', fontSize:13, fontWeight:700, cursor: saving ? 'wait' : 'pointer', display:'flex', alignItems:'center', gap:6 }}>
          <Plus size={13} strokeWidth={2.2}/>{saving ? 'Saving…' : product ? 'Save product' : 'Create product'}
        </button>
      </div>
    </form>
  );
};

const defaultBundleRows = products => [
  { kind: products.length ? 'existing' : 'new', productId: products[0]?.id || '', name:'', sku:'', quantity:1 },
  { kind:'new', productId:'', name:'', sku:'', quantity:1 },
];

const existingBundleRowsFor = (products, rawName, source) => {
  const name = normalizeProductName(rawName);
  if (!name) return null;
  const rows = [];
  for (const p of products) {
    for (const alias of p.bundle_aliases || []) {
      if (normalizeProductName(alias.raw_name) === name && (!alias.source || !source || alias.source === source)) {
        rows.push({ kind:'existing', productId: p.id, name:'', sku:'', quantity: alias.quantity || 1 });
      }
    }
  }
  return rows.length ? rows : null;
};

const ProductAssignmentModal = ({ order, item, products, onClose, onAssigned }) => {
  const [mode, setMode] = useState(products.length ? 'existing' : 'new');
  const [productId, setProductId] = useState(products[0]?.id || '');
  const targetItem = order ? (item || orderItemsForDisplay(order).find(i => !i.product_id) || orderItemsForDisplay(order)[0]) : null;
  const rawName = targetItem?.raw_name || targetItem?.display_name || order?.product;
  const [bundleRows, setBundleRows] = useState(() => existingBundleRowsFor(products, rawName, order?.source) || defaultBundleRows(products));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [checkingImpact, setCheckingImpact] = useState(false);
  const [pendingComponents, setPendingComponents] = useState(null);
  const [impact, setImpact] = useState(null);
  if (!order) return null;
  const bundlePath = targetItem?.id ? `/api/order-items/${encodeURIComponent(targetItem.id)}/bundle` : `/api/orders/${encodeURIComponent(order.id)}/bundle`;
  const bundleImpactPath = targetItem?.id ? `/api/order-items/${encodeURIComponent(targetItem.id)}/bundle-impact` : `/api/orders/${encodeURIComponent(order.id)}/bundle-impact`;

  const assign = async id => {
    setSaving(true);
    const path = targetItem?.id ? `/api/order-items/${encodeURIComponent(targetItem.id)}/product` : `/api/orders/${encodeURIComponent(order.id)}/product`;
    const { ok } = await api('POST', path, { product_id: id });
    setSaving(false);
    if (ok) onAssigned?.(id);
  };

  const updateBundleRow = (index, patch) => {
    setBundleRows(rows => rows.map((row, i) => i === index ? { ...row, ...patch } : row));
  };

  const addBundleRow = () => {
    setBundleRows(rows => [...rows, { kind: products.length ? 'existing' : 'new', productId: products[0]?.id || '', name:'', sku:'', quantity:1 }]);
  };

  const removeBundleRow = index => {
    setBundleRows(rows => rows.length > 1 ? rows.filter((_, i) => i !== index) : rows);
  };

  const commitBundle = async (components, applyToAll) => {
    setSaving(true);
    setError('');
    const { ok, data } = await api('POST', bundlePath, { components, applyToAll });
    setSaving(false);
    if (!ok) { setError(data.error || 'Could not save bundle'); return; }
    setPendingComponents(null);
    setImpact(null);
    onAssigned?.();
  };

  const saveBundle = async () => {
    setSaving(true);
    setError('');
    const components = [];
    for (const row of bundleRows) {
      const quantity = Math.max(1, Number.parseInt(row.quantity, 10) || 1);
      if (row.kind === 'existing') {
        if (!row.productId) { setError('Choose a product for every bundle row'); setSaving(false); return; }
        components.push({ product_id: row.productId, quantity });
      } else {
        const name = normalizeProductName(row.name);
        if (!name) { setError('Name every new product in the bundle'); setSaving(false); return; }
        const { ok, data } = await api('POST', '/api/products', { name, sku: row.sku || '', aliases: [] });
        if (!ok) { setError(data.error || 'Could not create bundle product'); setSaving(false); return; }
        components.push({ product_id: data.product.id, quantity });
      }
    }
    setCheckingImpact(true);
    const { ok, data } = await api('GET', bundleImpactPath);
    setCheckingImpact(false);
    const affectedOrders = ok ? (data.affectedOrders || 0) : 0;
    setSaving(false);
    if (affectedOrders > 0) {
      // Other existing orders share this exact item name — ask before touching them.
      setPendingComponents(components);
      setImpact(affectedOrders);
      return;
    }
    await commitBundle(components, false);
  };

  const bundleInput = { width:'100%', border:'1px solid #E5E7EB', borderRadius:8, padding:'8px 10px', fontSize:13, outline:'none', background:'#fff' };

  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.2)', zIndex:60 }}></div>
      <div style={{ position:'fixed', top:'50%', left:'50%', transform:'translate(-50%, -50%)', width:'min(520px, calc(100vw - 32px))', background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, boxShadow:'0 18px 60px rgba(0,0,0,0.18)', zIndex:70, overflow:'hidden' }}>
        <div style={{ padding:'18px 20px', borderBottom:'1px solid #F3F4F6', display:'flex', justifyContent:'space-between', gap:16 }}>
          <div>
            <div style={{ fontSize:16, fontWeight:800, color:'#1A1A2E' }}>Define item</div>
            <div style={{ fontSize:12, color:'#6B7280', marginTop:4 }}>Raw item name: <span style={{ fontWeight:700, color:'#374151' }}>{rawName || 'N/A'}</span></div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:22, cursor:'pointer', color:'#9CA3AF', lineHeight:1 }}>×</button>
        </div>
        <div style={{ padding:20 }}>
          <div style={{ display:'flex', background:'#F3F4F6', borderRadius:8, padding:3, marginBottom:16 }}>
            <button onClick={() => setMode('existing')} disabled={!products.length} style={{ flex:1, border:'none', borderRadius:6, padding:'7px 8px', background: mode === 'existing' ? '#fff' : 'transparent', color: products.length ? '#374151' : '#9CA3AF', fontSize:13, fontWeight:600, cursor: products.length ? 'pointer' : 'not-allowed' }}>Existing</button>
            <button onClick={() => setMode('new')} style={{ flex:1, border:'none', borderRadius:6, padding:'7px 8px', background: mode === 'new' ? '#fff' : 'transparent', color:'#374151', fontSize:13, fontWeight:600, cursor:'pointer' }}>New product</button>
            <button onClick={() => setMode('bundle')} style={{ flex:1, border:'none', borderRadius:6, padding:'7px 8px', background: mode === 'bundle' ? '#fff' : 'transparent', color:'#374151', fontSize:13, fontWeight:600, cursor:'pointer' }}>Bundle</button>
          </div>
          {mode === 'existing' ? (
            <div style={{ display:'grid', gap:12 }}>
              <select value={productId} onChange={e => setProductId(e.target.value)} style={{ width:'100%', border:'1px solid #E5E7EB', borderRadius:8, padding:'9px 11px', fontSize:13, outline:'none', background:'#fff' }}>
                {products.map(p => <option key={p.id} value={p.id}>{p.name}{p.sku ? ` (${p.sku})` : ''}</option>)}
              </select>
              <button onClick={() => assign(productId)} disabled={saving || !productId} style={{ background:'#006FDD', color:'#fff', border:'none', borderRadius:8, padding:10, fontSize:13, fontWeight:700, cursor: saving ? 'wait' : 'pointer' }}>
                {saving ? 'Assigning…' : 'Assign product'}
              </button>
            </div>
          ) : (
            mode === 'new' ? (
              <ProductForm rawName={rawName} onSaved={p => assign(p.id)} onCancel={products.length ? () => setMode('existing') : null}/>
            ) : (
              <div style={{ display:'grid', gap:12 }}>
                {impact != null ? (
                  <div style={{ border:'1px solid #FDE68A', background:'#FFFBEB', borderRadius:9, padding:12, display:'grid', gap:10 }}>
                    <div style={{ fontSize:13, color:'#92400E', fontWeight:800, lineHeight:1.4 }}>
                      {impact} other existing order{impact === 1 ? '' : 's'} also {impact === 1 ? 'has' : 'have'} an item named "{rawName}". Apply this bundle to those too?
                    </div>
                    <div style={{ fontSize:12, color:'#92400E', lineHeight:1.45 }}>
                      This cannot be reverted. If you're not sure, choose "No" — it will only change the order you're viewing now.
                    </div>
                    {error && <div style={{ color:'#DC2626', fontSize:12 }}>{error}</div>}
                    <div style={{ display:'flex', justifyContent:'flex-end', gap:8, flexWrap:'wrap' }}>
                      <button type="button" onClick={() => { setPendingComponents(null); setImpact(null); }} disabled={saving} style={{ background:'#fff', color:'#374151', border:'1px solid #E5E7EB', borderRadius:8, padding:'9px 13px', fontSize:13, fontWeight:700, cursor:saving ? 'not-allowed' : 'pointer' }}>
                        Cancel
                      </button>
                      <button type="button" onClick={() => commitBundle(pendingComponents, true)} disabled={saving} style={{ background:'#fff', color:'#B91C1C', border:'1px solid #FECACA', borderRadius:8, padding:'9px 13px', fontSize:13, fontWeight:800, cursor:saving ? 'wait' : 'pointer' }}>
                        {saving ? 'Saving...' : `Yes, apply to all ${impact}`}
                      </button>
                      <button type="button" autoFocus onClick={() => commitBundle(pendingComponents, false)} disabled={saving} style={{ background: saving ? '#93C5FD' : '#006FDD', color:'#fff', border:'none', borderRadius:8, padding:'9px 13px', fontSize:13, fontWeight:800, cursor:saving ? 'wait' : 'pointer' }}>
                        {saving ? 'Saving...' : 'No, just this order'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize:12, color:'#6B7280', lineHeight:1.45 }}>
                      This changes the order you're viewing. If other existing orders share this exact item name, you'll be asked before they're changed too.
                    </div>
                    <div style={{ display:'grid', gap:8 }}>
                      {bundleRows.map((row, index) => (
                        <div key={index} style={{ border:'1px solid #E5E7EB', borderRadius:9, padding:10, display:'grid', gap:8, background:index % 2 ? '#FAFAFA' : '#fff' }}>
                          <div style={{ display:'grid', gridTemplateColumns:'minmax(86px, 112px) minmax(0, 1fr) 70px auto', gap:8, alignItems:'center' }}>
                            <select value={row.kind} onChange={e => updateBundleRow(index, { kind:e.target.value })} style={bundleInput}>
                              {products.length > 0 && <option value="existing">Existing</option>}
                              <option value="new">New</option>
                            </select>
                            {row.kind === 'existing' ? (
                              <select value={row.productId} onChange={e => updateBundleRow(index, { productId:e.target.value })} style={bundleInput}>
                                {products.map(p => <option key={p.id} value={p.id}>{p.name}{p.sku ? ` (${p.sku})` : ''}</option>)}
                              </select>
                            ) : (
                              <input value={row.name} onChange={e => updateBundleRow(index, { name:e.target.value })} style={bundleInput} placeholder="New product name"/>
                            )}
                            <input type="number" min="1" value={row.quantity} onChange={e => updateBundleRow(index, { quantity:e.target.value })} style={bundleInput} title="Quantity"/>
                            <button type="button" onClick={() => removeBundleRow(index)} title="Remove product" style={{ background:'#fff', border:'1px solid #FEE2E2', color:'#DC2626', borderRadius:7, padding:8, cursor:'pointer', display:'flex' }}>
                              <Trash2 size={13} strokeWidth={2}/>
                            </button>
                          </div>
                          {row.kind === 'new' && (
                            <input value={row.sku} onChange={e => updateBundleRow(index, { sku:e.target.value })} style={bundleInput} placeholder="SKU optional"/>
                          )}
                        </div>
                      ))}
                    </div>
                    {error && <div style={{ color:'#DC2626', fontSize:12 }}>{error}</div>}
                    <div style={{ display:'flex', justifyContent:'space-between', gap:10 }}>
                      <button type="button" onClick={addBundleRow} style={{ background:'#fff', color:'#374151', border:'1px solid #E5E7EB', borderRadius:8, padding:'9px 12px', fontSize:13, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}>
                        <Plus size={13} strokeWidth={2.2}/> Product
                      </button>
                      <button type="button" onClick={saveBundle} disabled={saving || checkingImpact} style={{ background: (saving || checkingImpact) ? '#93C5FD' : '#006FDD', color:'#fff', border:'none', borderRadius:8, padding:'9px 13px', fontSize:13, fontWeight:800, cursor:(saving || checkingImpact) ? 'wait' : 'pointer', display:'flex', alignItems:'center', gap:6 }}>
                        <Package size={14} strokeWidth={2.2}/>{checkingImpact ? 'Checking...' : saving ? 'Saving...' : 'Save bundle'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )
          )}
        </div>
      </div>
    </>
  );
};

const ProductModal = ({ product, onClose, onSaved }) => {
  if (!product && product !== null) return null;
  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.2)', zIndex:60 }}></div>
      <div style={{ position:'fixed', top:'50%', left:'50%', transform:'translate(-50%, -50%)', width:'min(520px, calc(100vw - 32px))', background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, boxShadow:'0 18px 60px rgba(0,0,0,0.18)', zIndex:70, overflow:'hidden' }}>
        <div style={{ padding:'18px 20px', borderBottom:'1px solid #F3F4F6', display:'flex', justifyContent:'space-between', gap:16 }}>
          <div>
            <div style={{ fontSize:16, fontWeight:800, color:'#1A1A2E' }}>{product ? 'Edit product' : 'Create product'}</div>
            <div style={{ fontSize:12, color:'#6B7280', marginTop:4 }}>Product name is the canonical item. Inventory count is optional stock tracking.</div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:22, cursor:'pointer', color:'#9CA3AF', lineHeight:1 }}>×</button>
        </div>
        <div style={{ padding:20 }}>
          <ProductForm product={product} onSaved={onSaved} onCancel={onClose}/>
        </div>
      </div>
    </>
  );
};

const ProductAliasesModal = ({ product, onClose, onChanged }) => {
  const [aliases, setAliases] = useState(() => product?.aliases || []);
  const [deletingAliasId, setDeletingAliasId] = useState(null);
  const [error, setError] = useState('');
  if (!product) return null;
  const removeAlias = async alias => {
    if (!alias?.id || deletingAliasId !== null) return;
    const sourceLabel = PLATFORM_CFG[alias.source]?.label || alias.source || 'any source';
    if (!confirm(`Remove the alias "${alias.raw_name}" from ${sourceLabel}? Existing orders using this alias will become unassigned.`)) return;
    setDeletingAliasId(alias.id);
    setError('');
    const { ok, data } = await api('DELETE', `/api/products/${product.id}/aliases/${alias.id}`);
    setDeletingAliasId(null);
    if (!ok) {
      setError(data?.error || 'Could not remove alias.');
      return;
    }
    setAliases(current => current.filter(item => item.id !== alias.id));
    onChanged?.();
  };
  return (
    <>
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.2)', zIndex:60 }}></div>
      <div style={{ position:'fixed', top:'50%', left:'50%', transform:'translate(-50%, -50%)', width:'min(500px, calc(100vw - 32px))', background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, boxShadow:'0 18px 60px rgba(0,0,0,0.18)', zIndex:70, overflow:'hidden' }}>
        <div style={{ padding:'18px 20px', borderBottom:'1px solid #F3F4F6', display:'flex', justifyContent:'space-between', gap:16 }}>
          <div>
            <div style={{ fontSize:16, fontWeight:800, color:'#1A1A2E' }}>Product aliases</div>
            <div style={{ fontSize:12, color:'#6B7280', marginTop:4 }}>{product.name}</div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:22, cursor:'pointer', color:'#9CA3AF', lineHeight:1 }}>×</button>
        </div>
        <div style={{ padding:20 }}>
          {aliases.length ? (
            <div style={{ display:'grid', gap:8 }}>
              {aliases.map(a => (
                <div key={a.id || `${a.raw_name}-${a.source || ''}`} style={{ display:'flex', alignItems:'center', gap:10, border:'1px solid #F3F4F6', borderRadius:8, padding:'7px 8px 7px 11px', background:'#FAFAFA' }}>
                  <span style={{ minWidth:0, flex:1, fontSize:13, fontWeight:600, color:'#374151', overflowWrap:'anywhere' }}>{a.raw_name}</span>
                  <span style={{ fontSize:12, color:'#9CA3AF', whiteSpace:'nowrap' }}>{PLATFORM_CFG[a.source]?.label || a.source || 'Any source'}</span>
                  <button
                    type="button"
                    onClick={() => removeAlias(a)}
                    disabled={deletingAliasId !== null}
                    title={`Remove ${a.raw_name} alias`}
                    aria-label={`Remove ${a.raw_name} alias`}
                    style={{ width:28, height:28, display:'inline-flex', alignItems:'center', justifyContent:'center', flexShrink:0, border:'none', borderRadius:6, background:'transparent', color:deletingAliasId === a.id ? '#CBD5E1' : '#9CA3AF', cursor:deletingAliasId === null ? 'pointer' : 'not-allowed' }}
                  >
                    <X size={14} strokeWidth={2.2}/>
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color:'#9CA3AF', fontSize:13 }}>No aliases yet. They are created when an order product name is assigned to this product.</div>
          )}
          {product.bundle_aliases?.length > 0 && (
            <div style={{ marginTop:16 }}>
              <div style={{ fontSize:11, fontWeight:800, color:'#6B7280', letterSpacing:'0.06em', marginBottom:8 }}>BUNDLE ITEMS</div>
              <div style={{ display:'grid', gap:8 }}>
                {product.bundle_aliases.map((a, index) => (
                  <div key={`${a.raw_name}-${a.source || ''}-${index}`} style={{ display:'flex', justifyContent:'space-between', gap:12, border:'1px solid #E0F2FE', borderRadius:8, padding:'9px 11px', background:'#F0F9FF' }}>
                    <span style={{ fontSize:13, fontWeight:700, color:'#075985' }}>{a.raw_name} <span style={{ fontSize:11, color:'#0284C7' }}>x{a.quantity}</span></span>
                    <span style={{ fontSize:12, color:'#0369A1', whiteSpace:'nowrap' }}>{PLATFORM_CFG[a.source]?.label || a.source || 'Any source'}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {error && <div style={{ marginTop:10, color:'#B91C1C', fontSize:12, fontWeight:700 }}>{error}</div>}
        </div>
      </div>
    </>
  );
};

const ProductCell = ({ order, onDefine }) => {
  const [expanded, setExpanded] = useState(false);
  const items = orderItemsForDisplay(order);
  const visibleItems = expanded ? items : items.slice(0, 3);
  if (!items.length || !items.some(item => normalizeProductName(item.raw_name || item.display_name))) {
    return (
      <button onClick={e => { e.stopPropagation(); onDefine?.(order); }} title="Add product details" style={{ width:'100%', minHeight:34, background:'#F9FAFB', border:'1px dashed #D1D5DB', borderRadius:7, padding:'6px 8px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between', gap:7, color:'#9CA3AF', fontSize:12, textAlign:'left', minWidth:0 }}>
        <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>N/A</span>
        <Edit3 size={13} strokeWidth={2} style={{ flexShrink:0 }}/>
      </button>
    );
  }
  return (
    <div style={{ display:'grid', gap:4, minWidth:0 }}>
      {items.length > 1 && (
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, minWidth:0 }}>
          <span style={{ fontSize:11, fontWeight:900, color:'#374151', whiteSpace:'nowrap' }}>{items.length} items</span>
          <span style={{ height:1, background:'#E5E7EB', flex:1, minWidth:16 }} />
        </div>
      )}
      {visibleItems.map(item => {
        const recognized = !!item.product_id;
        return (
          <button key={item.id || item.position || item.display_name} onClick={e => { e.stopPropagation(); onDefine?.({ order, item }); }} title={recognized ? 'Change item mapping' : 'Define this item'} style={{ width:'100%', minHeight:26, background: recognized ? '#F9FAFB' : '#FFFBEB', border:`1px solid ${recognized ? '#E5E7EB' : '#FDE68A'}`, borderRadius:7, padding:'4px 7px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'space-between', gap:7, color: recognized ? '#374151' : '#B45309', fontSize:12, textAlign:'left', minWidth:0 }}>
            <span style={{ display:'flex', alignItems:'center', gap:6, minWidth:0 }}>
              {!recognized && <AlertTriangle size={13} strokeWidth={2.2} color="#F59E0B" style={{ flexShrink:0 }}/>}
              {recognized && <ProductThumb src={item.product_image_data} size={20}/>}
              <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{item.display_name}</span>
            </span>
            <span style={{ flexShrink:0, color: recognized ? '#6B7280' : '#B45309', fontSize:11, fontWeight:800 }}>x{item.quantity}</span>
          </button>
        );
      })}
      {items.length > 3 && (
        <button onClick={e => { e.stopPropagation(); setExpanded(v => !v); }} style={{ border:'none', background:'transparent', color:'#006FDD', fontSize:11, fontWeight:800, cursor:'pointer', textAlign:'left', padding:'1px 2px' }}>
          {expanded ? 'Show first 3' : `+${items.length - 3} more`}
        </button>
      )}
    </div>
  );
};

const PRODUCTS_PAGE_SIZE = 25;
const PRODUCTS_PERIOD_DAYS = 30;
const PRODUCTS_DAY_MS = 24 * 60 * 60 * 1000;
const PRODUCT_SORT_OPTIONS = [
  { value:'revenue:desc', label:'Revenue ↓' },
  { value:'revenue:asc', label:'Revenue ↑' },
  { value:'orders:desc', label:'Orders ↓' },
  { value:'orders:asc', label:'Orders ↑' },
  { value:'last_order:desc', label:'Last order ↓' },
  { value:'last_order:asc', label:'Last order ↑' },
  { value:'stock:asc', label:'Stock low first' },
  { value:'stock:desc', label:'Stock high first' },
  { value:'product:asc', label:'Product A-Z' },
  { value:'product:desc', label:'Product Z-A' },
];
const PRODUCT_HEADER_SORT_DEFAULTS = {
  product: 'asc',
  orders: 'desc',
  revenue: 'desc',
  last_order: 'desc',
  stock: 'asc',
};

const productCountText = value => Number(value || 0).toLocaleString('en-US');
const productNumber = value => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};
const productMoneyNumber = value => {
  const n = parseFloat(String(value || '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
};
const productCurrency = value => String(value || 'usd').toLowerCase();
const productIsInactive = product => Number(product.order_count || 0) <= 0 && Math.abs(productNumber(product.revenue)) === 0;
const productLastOrderDate = product => parseOrderDate({ date: product.last_order_date, created_at: product.last_order_created_at });
const productAbsoluteDateTitle = date => {
  if (!date) return '';
  try {
    return new Intl.DateTimeFormat(undefined, { dateStyle:'medium', timeStyle:'short' }).format(date);
  } catch {
    return date.toISOString();
  }
};
const productTrendDelta = (current, previous) => {
  const cur = productNumber(current);
  const prev = productNumber(previous);
  let pct = 0;
  if (prev === 0 && cur > 0) pct = 100;
  else if (prev !== 0) pct = ((cur - prev) / Math.abs(prev)) * 100;
  const rounded = Math.round(Math.abs(pct));
  if (pct > 0) return { text:`▲ ${rounded}%`, color:'#475569' };
  if (pct < 0) return { text:`▼ ${rounded}%`, color:'#B45309' };
  return { text:'0%', color:'#64748B' };
};
const productStockInfo = product => {
  if (!product.track_stock) return { tracked:false, status:'off', label:'-', pct:null, roundedPct:null, left:null, total:null, color:'#94A3B8', text:'#94A3B8' };
  const total = Math.max(0, productNumber(product.stock_qty));
  const left = Math.max(0, productNumber(product.remaining_stock));
  const threshold = Math.max(0, productNumber(product.low_stock_threshold));
  const pct = total > 0 ? Math.max(0, Math.min(100, left / total * 100)) : 0;
  const roundedPct = Math.round(pct);
  const out = left <= 0 || total <= 0;
  const low = !out && left <= threshold;
  const status = out ? 'out' : low ? 'low' : 'in_stock';
  const color = out ? '#DC2626' : low ? '#F59E0B' : '#94A3B8';
  const text = out ? '#B91C1C' : low ? '#92400E' : '#475569';
  return {
    tracked:true,
    status,
    label:`${left} / ${total} · ${roundedPct}%`,
    pct,
    roundedPct,
    left,
    total,
    threshold,
    color,
    text,
  };
};
const productIssueReasons = product => {
  const reasons = [];
  if (!String(product.sku || '').trim()) reasons.push('No SKU');
  if (!product.image_data) reasons.push('Missing image');
  const stock = productStockInfo(product);
  if (stock.tracked && stock.status === 'out') reasons.push('Out of stock');
  return reasons;
};
const productSearchText = product => [
  product.name,
  product.sku,
  ...(product.aliases || []).map(alias => `${alias.raw_name || ''} ${alias.source || ''}`),
  ...(product.bundle_aliases || []).map(alias => `${alias.raw_name || ''} ${alias.source || ''}`),
].join(' ').toLowerCase();
const productRevenueRowsFromOrders = (orders, start = null, end = null) => orders.reduce((acc, order) => {
  const status = normalizeDashboardOrderStatus(order.status);
  if (status === 'ignored' || status === 'problem') return acc;
  const date = parseOrderDate(order);
  if (start && (!date || date < start)) return acc;
  if (end && (!date || date > end)) return acc;
  const amount = productMoneyNumber(order.amount);
  if (!amount) return acc;
  const items = orderItemsForDisplay(order).filter(item => item.product_id);
  if (!items.length) return acc;
  const currency = productCurrency(order.currency);
  const itemRevenue = amount / items.length;
  items.forEach(item => {
    const productId = String(item.product_id);
    acc[productId] = acc[productId] || {};
    acc[productId][currency] = acc[productId][currency] || { currency, revenue: 0, count: 0 };
    acc[productId][currency].revenue += itemRevenue;
    acc[productId][currency].count += 1;
  });
  return acc;
}, {});
const aggregateProductRevenueRows = rows => Object.values(rows.reduce((acc, row) => {
  const currency = productCurrency(row.currency);
  acc[currency] = acc[currency] || { currency, revenue: 0, count: 0 };
  acc[currency].revenue += productNumber(row.revenue);
  acc[currency].count += productNumber(row.count);
  return acc;
}, {})).sort((a, b) => Math.abs(b.revenue) - Math.abs(a.revenue) || b.count - a.count);
const primaryProductRevenueRow = rows => aggregateProductRevenueRows(rows)[0] || { currency:'usd', revenue:0, count:0 };
const revenueRowsForProducts = (products, productRevenueRows) => {
  const rows = [];
  products.forEach(product => {
    const productRows = Object.values(productRevenueRows[String(product.id)] || {});
    if (productRows.length) rows.push(...productRows);
    else if (productNumber(product.revenue)) rows.push({ currency: product.revenue_currency || 'usd', revenue: productNumber(product.revenue), count: productNumber(product.order_count) });
  });
  return aggregateProductRevenueRows(rows);
};

const ProductsScreen = ({ user, products, orders, ordersLoading = false, onProductsChanged }) => {
  const [modalProduct, setModalProduct] = useState(undefined);
  const [aliasProduct, setAliasProduct] = useState(null);
  const [search, setSearch] = useState('');
  const [stockFilter, setStockFilter] = useState('all');
  const [sortValue, setSortValue] = useState('revenue:desc');
  const [showInactive, setShowInactive] = useState(false);
  const [showStockApiInfo, setShowStockApiInfo] = useState(false);
  const [page, setPage] = useState(1);
  const [hoveredProductId, setHoveredProductId] = useState(null);

  const unassigned = orders.filter(orderNeedsProductDefinition);
  const inactiveProducts = products.filter(productIsInactive);
  const inactiveCount = inactiveProducts.length;
  const activeCount = Math.max(0, products.length - inactiveCount);
  const hasInactive = inactiveCount > 0;
  const initialLoading = ordersLoading && products.length === 0;
  const baseRevenueLoading = ordersLoading && orders.length === 0;
  const [sortKey, sortDir = 'asc'] = sortValue.split(':');

  const now = new Date();
  const currentStart = new Date(now.getTime() - PRODUCTS_PERIOD_DAYS * PRODUCTS_DAY_MS);
  const priorStart = new Date(currentStart.getTime() - PRODUCTS_PERIOD_DAYS * PRODUCTS_DAY_MS);
  const productRevenueRows = productRevenueRowsFromOrders(orders);
  const currentProductRevenueRows = productRevenueRowsFromOrders(orders, currentStart, now);
  const priorProductRevenueRows = productRevenueRowsFromOrders(orders, priorStart, currentStart);
  const currentRevenueRows = aggregateProductRevenueRows(Object.values(currentProductRevenueRows).flatMap(row => Object.values(row)));
  const priorRevenueRows = aggregateProductRevenueRows(Object.values(priorProductRevenueRows).flatMap(row => Object.values(row)));
  const dominantProductRevenue = primaryProductRevenueRow(currentRevenueRows.length ? currentRevenueRows : priorRevenueRows);
  const revenueCurrency = productCurrency(user?.preferredAnalyticsCurrency || dominantProductRevenue.currency || 'usd');
  const convertedRevenueMixing = user?.convertedRevenueMixing !== false;
  const allProductRevenueRows = Object.values(productRevenueRows).flatMap(row => Object.values(row));
  const hasForeignRevenue = [...allProductRevenueRows, ...currentRevenueRows, ...priorRevenueRows]
    .some(row => productCurrency(row.currency) !== revenueCurrency && productNumber(row.revenue));
  const [productExchangeRates, setProductExchangeRates] = useState(null);
  useEffect(() => {
    let active = true;
    if (!convertedRevenueMixing || !hasForeignRevenue) {
      setProductExchangeRates({ [revenueCurrency]:1 });
      return () => { active = false; };
    }
    setProductExchangeRates(null);
    api('GET', `/api/analytics/exchange-rates?base=${encodeURIComponent(revenueCurrency)}`).then(({ ok, data }) => {
      if (!active) return;
      setProductExchangeRates(ok && data?.rates ? data.rates : { [revenueCurrency]:1 });
    }).catch(() => {
      if (active) setProductExchangeRates({ [revenueCurrency]:1 });
    });
    return () => { active = false; };
  }, [convertedRevenueMixing, hasForeignRevenue, revenueCurrency]);
  const convertProductRevenueRows = rows => ({
    currency:revenueCurrency,
    revenue:(rows || []).reduce((sum, row) => {
      const currency = productCurrency(row.currency);
      if (currency === revenueCurrency) return sum + productNumber(row.revenue);
      if (!convertedRevenueMixing) return sum;
      const rate = Number(productExchangeRates?.[currency]);
      return Number.isFinite(rate) && rate > 0 ? sum + productNumber(row.revenue) * rate : sum;
    }, 0),
    count:(rows || []).reduce((sum, row) => sum + productNumber(row.count), 0),
  });
  const currentPrimaryRevenue = convertProductRevenueRows(currentRevenueRows);
  const priorPrimaryRevenue = convertProductRevenueRows(priorRevenueRows);
  const revenueLoading = baseRevenueLoading || (convertedRevenueMixing && hasForeignRevenue && productExchangeRates === null);
  const lowStockCount = products.filter(product => {
    const stock = productStockInfo(product);
    return stock.tracked && stock.status === 'low';
  }).length;
  const productsWithIssues = products.filter(product => productIssueReasons(product).length > 0);
  const issueCount = productsWithIssues.length;

  const clearFilters = () => {
    setSearch('');
    setStockFilter('all');
    setSortValue('revenue:desc');
    setShowInactive(activeCount === 0 && inactiveCount > 0);
  };

  useEffect(() => {
    setPage(1);
  }, [search, stockFilter, sortValue, showInactive, products.length]);

  const filteredProducts = products.filter(product => {
    if (!showInactive && productIsInactive(product)) return false;
    const query = search.trim().toLowerCase();
    if (query && !productSearchText(product).includes(query)) return false;
    const stock = productStockInfo(product);
    if (stockFilter !== 'all' && stock.status !== stockFilter) return false;
    return true;
  });

  const sortValueForProduct = product => {
    if (sortKey === 'product') return String(product.name || '').toLowerCase();
    if (sortKey === 'orders') return productNumber(product.order_count);
    if (sortKey === 'revenue') {
      const rows = Object.values(productRevenueRows[String(product.id)] || {});
      const revenueRows = rows.length ? rows : [{ currency:product.revenue_currency || 'usd', revenue:product.revenue, count:product.order_count }];
      return convertProductRevenueRows(revenueRows).revenue;
    }
    if (sortKey === 'last_order') {
      const date = productLastOrderDate(product);
      if (date) return date.getTime();
      return sortDir === 'asc' ? Number.POSITIVE_INFINITY : 0;
    }
    if (sortKey === 'stock') {
      const stock = productStockInfo(product);
      if (!stock.tracked) return sortDir === 'asc' ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
      return stock.pct;
    }
    return String(product.name || '').toLowerCase();
  };

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (sortKey === 'revenue' && sortDir === 'desc') {
      const aInactive = productIsInactive(a);
      const bInactive = productIsInactive(b);
      if (aInactive !== bInactive) return aInactive ? 1 : -1;
    }
    const av = sortValueForProduct(a);
    const bv = sortValueForProduct(b);
    let result = 0;
    if (typeof av === 'string' || typeof bv === 'string') result = String(av).localeCompare(String(bv));
    else result = av === bv ? 0 : av > bv ? 1 : -1;
    if (result === 0) result = String(a.name || '').localeCompare(String(b.name || ''));
    return sortDir === 'asc' ? result : -result;
  });

  const totalPages = Math.max(1, Math.ceil(sortedProducts.length / PRODUCTS_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PRODUCTS_PAGE_SIZE;
  const pagedProducts = sortedProducts.slice(pageStart, pageStart + PRODUCTS_PAGE_SIZE);
  const filteredRevenueRows = revenueRowsForProducts(sortedProducts, productRevenueRows);
  const filteredPrimaryRevenue = convertProductRevenueRows(filteredRevenueRows);
  const filteredOrderTotal = sortedProducts.reduce((sum, product) => sum + productNumber(product.order_count), 0);
  const productSummaryText = `${productCountText(products.length)} product${products.length === 1 ? '' : 's'} · ${productCountText(activeCount)} active`;
  const filtersActive = !!search.trim() || stockFilter !== 'all' || sortValue !== 'revenue:desc' || showInactive !== (activeCount === 0 && inactiveCount > 0);

  const kpiCards = [
    {
      label: 'Total products',
      value: initialLoading ? null : productCountText(products.length),
      meta: initialLoading ? 'loading catalog' : `${productCountText(activeCount)} active · ${productCountText(inactiveCount)} inactive`,
      Icon: Package,
      trendLabel: 'catalog',
      skeletonWidth: 44,
    },
    {
      label: 'Total revenue',
      value: initialLoading || revenueLoading ? null : formatMoneyCompact(currentPrimaryRevenue.revenue, revenueCurrency),
      meta: 'last 30 days',
      Icon: DollarSign,
      windowed: true,
      trend: initialLoading || revenueLoading ? null : productTrendDelta(currentPrimaryRevenue.revenue, priorPrimaryRevenue.revenue),
      extra: !initialLoading && !revenueLoading ? <CurrencyBreakdownBadge rows={currentRevenueRows}/> : null,
      skeletonWidth: 76,
    },
    {
      label: 'Low stock',
      value: initialLoading ? null : productCountText(lowStockCount),
      meta: 'at or below each product threshold',
      Icon: AlertTriangle,
      valueColor: lowStockCount > 0 ? '#B45309' : '#0F172A',
      iconColor: lowStockCount > 0 ? '#D97706' : '#64748B',
      trendLabel: lowStockCount > 0 ? 'review' : 'clear',
      skeletonWidth: 34,
    },
    {
      label: 'Unmapped / issues',
      value: initialLoading ? null : productCountText(issueCount),
      meta: unassigned.length ? `${productCountText(unassigned.length)} unmapped order${unassigned.length === 1 ? '' : 's'}` : 'SKU, image, and stock setup',
      Icon: AlertTriangle,
      valueColor: issueCount > 0 ? '#B45309' : '#0F172A',
      iconColor: issueCount > 0 ? '#D97706' : '#64748B',
      trendLabel: issueCount > 0 ? 'review' : 'clear',
      skeletonWidth: 34,
    },
  ];

  const remove = async product => {
    if (!confirm(`Delete ${product.name}? Orders assigned to it will become unassigned.`)) return;
    const { ok } = await api('DELETE', `/api/products/${product.id}`);
    if (ok) onProductsChanged?.();
  };

  const toggleHeaderSort = column => {
    const nextDir = sortKey === column
      ? sortDir === 'asc' ? 'desc' : 'asc'
      : PRODUCT_HEADER_SORT_DEFAULTS[column] || 'asc';
    setSortValue(`${column}:${nextDir}`);
  };

  const SortHeader = ({ column, label, align = 'left' }) => {
    const active = sortKey === column;
    return (
      <button
        type="button"
        onClick={() => toggleHeaderSort(column)}
        aria-sort={active ? sortDir === 'asc' ? 'ascending' : 'descending' : 'none'}
        style={{
          width:'100%',
          border:'none',
          background:'transparent',
          padding:0,
          display:'inline-flex',
          alignItems:'center',
          justifyContent:align === 'right' ? 'flex-end' : 'flex-start',
          gap:4,
          color:active ? '#475569' : '#94A3B8',
          fontSize:11,
          fontWeight:850,
          letterSpacing:'0.04em',
          textTransform:'uppercase',
          cursor:'pointer',
          whiteSpace:'nowrap',
        }}
      >
        {label}
        <ChevronDown size={12} strokeWidth={2.4} style={{ opacity:active ? 1 : 0.35, transform:active && sortDir === 'asc' ? 'rotate(180deg)' : 'none', transition:'transform 120ms ease' }}/>
      </button>
    );
  };

  const stockCell = product => {
    const stock = productStockInfo(product);
    if (!stock.tracked) return <span title="Inventory tracking is off" style={{ color:'#94A3B8', fontWeight:800 }}>-</span>;
    return (
      <div title={`Low stock at ${stock.threshold} remaining unit${stock.threshold === 1 ? '' : 's'}`} style={{ width:128 }}>
        <div style={{ color:stock.text, fontSize:11.5, fontWeight:850, fontVariantNumeric:'tabular-nums', whiteSpace:'nowrap' }}>{stock.label}</div>
        <div style={{ height:5, width:'100%', background:'#E5E7EB', borderRadius:999, overflow:'hidden', marginTop:5 }}>
          <div style={{ height:'100%', width:`${Math.max(0, Math.min(100, stock.pct))}%`, background:stock.color, borderRadius:999 }}></div>
        </div>
      </div>
    );
  };

  const revenueCell = product => {
    if (revenueLoading) return <SkeletonPill width={72} height={13}/>;
    const rows = Object.values(productRevenueRows[String(product.id)] || {});
    const revenueRows = rows.length ? rows : [{ currency:product.revenue_currency || 'usd', revenue:product.revenue, count:product.order_count }];
    const converted = convertProductRevenueRows(revenueRows);
    if (!converted.revenue) return <span style={{ color:'#94A3B8', fontWeight:800 }}>-</span>;
    return (
      <span style={{ display:'inline-flex', alignItems:'center', gap:5, fontWeight:850, fontVariantNumeric:'tabular-nums', color:'#0F172A' }}>
        {formatMoneyCompact(converted.revenue, revenueCurrency)}
        <CurrencyBreakdownBadge rows={rows}/>
      </span>
    );
  };

  const lastOrderCell = product => {
    const date = productLastOrderDate(product);
    if (!date) return <span style={{ color:'#94A3B8', fontWeight:800 }}>-</span>;
    return <span title={productAbsoluteDateTitle(date)} style={{ fontWeight:800, color:'#475569', whiteSpace:'nowrap' }}>{timeAgo(date)}</span>;
  };

  const EmptyState = () => (
    <div style={{ padding:'46px 18px 50px', textAlign:'center', display:'grid', justifyItems:'center', gap:8 }}>
      <div style={{ fontSize:17, fontWeight:900, color:'#0F172A' }}>No products yet</div>
      <div style={{ fontSize:13, color:'#64748B', lineHeight:1.45, maxWidth:420 }}>Create products, then map incoming order item names to the catalog records your fulfillment workflow uses.</div>
      <button onClick={() => setModalProduct(null)} style={{ marginTop:8, border:'none', background:'#006FDD', color:'#fff', borderRadius:8, padding:'9px 13px', fontSize:13, fontWeight:850, cursor:'pointer', display:'inline-flex', alignItems:'center', gap:6 }}>
        <Plus size={14} strokeWidth={2.3}/> Add your first product
      </button>
    </div>
  );

  const NoResultsState = () => (
    <div style={{ padding:'42px 18px 46px', textAlign:'center', display:'grid', justifyItems:'center', gap:8 }}>
      <div style={{ fontSize:16, fontWeight:900, color:'#0F172A' }}>No products match these filters</div>
      <div style={{ fontSize:13, color:'#64748B' }}>Clear the current search, filters, and sort to return to the default catalog view.</div>
      <button onClick={clearFilters} style={{ marginTop:6, border:'1px solid #D1D5DB', background:'#fff', color:'#374151', borderRadius:8, padding:'8px 12px', fontSize:13, fontWeight:850, cursor:'pointer' }}>Clear filters</button>
    </div>
  );

  const TableSkeleton = () => (
    <table style={{ width:'100%', minWidth:980, borderCollapse:'collapse', fontSize:13 }}>
      <thead>
        <tr style={{ background:'#F8FAFC', borderBottom:'1px solid #E5E7EB' }}>
          {['Product','Orders','Revenue','Last order','Stock','Actions'].map(header => (
            <th key={header} style={{ padding:'9px 12px', textAlign:'left', fontSize:11, fontWeight:850, color:'#94A3B8', letterSpacing:'0.04em', textTransform:'uppercase' }}>{header}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: 7 }).map((_, index) => (
          <tr key={index} style={{ borderBottom:'1px solid #F1F5F9', background:index % 2 ? '#FAFAFA' : '#fff' }}>
            <td style={{ padding:'8px 12px' }}><div style={{ display:'flex', alignItems:'center', gap:9 }}><SkeletonPill width={32} height={32} style={{ borderRadius:7 }}/><div style={{ display:'grid', gap:5, width:'70%' }}><SkeletonPill width="82%" height={12}/><SkeletonPill width="46%" height={10}/></div></div></td>
            <td style={{ padding:'8px 12px' }}><SkeletonPill width={34} height={12}/></td>
            <td style={{ padding:'8px 12px' }}><SkeletonPill width={72} height={12}/></td>
            <td style={{ padding:'8px 12px' }}><SkeletonPill width={70} height={12}/></td>
            <td style={{ padding:'8px 12px' }}><SkeletonPill width={128} height={18} style={{ borderRadius:4 }}/></td>
            <td style={{ padding:'8px 12px' }}><SkeletonPill width={58} height={24} style={{ borderRadius:7 }}/></td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const selectStyle = {
    border:'1px solid #DDE3EA',
    background:'#fff',
    color:'#334155',
    borderRadius:8,
    padding:'8px 10px',
    minHeight:36,
    fontSize:13,
    fontWeight:750,
    outline:'none',
  };

  return (
    <div className="dash-page-scroll" style={{
      padding:'0 0 28px',
      maxWidth:'none',
      margin:0,
      minHeight:'100%',
      position:'relative',
      overflowX:'hidden',
      background:'linear-gradient(180deg, #F8FBFF 0%, #F8FAFC 48%, #FFFFFF 100%)',
    }}>
      <svg className="home-brush-layer" aria-hidden="true" viewBox="0 0 1200 740" preserveAspectRatio="none">
        <path className="home-brush-stripe home-brush-stripe-blue home-brush-stripe-a" d="M-120 170 L145 58 L350 205 L585 82 L805 250 L1115 116 L1320 265"/>
        <path className="home-brush-stripe home-brush-stripe-green home-brush-stripe-b" d="M-130 555 L130 370 L340 500 L560 315 L785 450 L1040 285 L1330 420"/>
        <path className="home-brush-stripe home-brush-stripe-blue home-brush-stripe-c" d="M40 785 L265 620 L505 710 L715 530 L940 670 L1240 505"/>
      </svg>

      <div style={{ position:'relative', zIndex:1, padding:'14px clamp(16px, 1.6vw, 28px) 0', width:'100%', boxSizing:'border-box', display:'grid', gap:12 }}>
        <section className="dash-panel workspace-panel-flat" style={{ overflow:'hidden' }}>
          <div className="dash-panel-header" style={{ display:'grid', gap:13, padding:'13px 14px' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:14, flexWrap:'wrap' }}>
              <div style={{ minWidth:0 }}>
                <h1 className="dash-page-title" style={{ display:'flex', alignItems:'baseline', gap:9, flexWrap:'wrap' }}>
                  Products
                  {initialLoading ? (
                    <SkeletonPill width={104} height={16}/>
                  ) : (
                    <span style={{ fontSize:12, fontWeight:800, color:'#94A3B8', whiteSpace:'nowrap' }}>{productSummaryText}</span>
                  )}
                </h1>
                <p className="dash-page-subtitle" style={{ maxWidth:760 }}>Track revenue, stock health, and fulfillment readiness across your catalog.</p>
              </div>
              <button onClick={() => setModalProduct(null)} style={{ background:'#006FDD', color:'#fff', border:'none', borderRadius:8, padding:'9px 13px', minHeight:36, fontSize:13, fontWeight:850, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}>
                <Plus size={14} strokeWidth={2.3}/> Product
              </button>
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 180px), 1fr))', gap:8, alignItems:'center' }}>
              <div style={{ position:'relative', minWidth:0 }}>
                <Search size={14} strokeWidth={2.2} color="#94A3B8" style={{ position:'absolute', left:11, top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }}/>
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search by name, SKU, or alias"
                  aria-label="Search products by name, SKU, or alias"
                  style={{ width:'100%', border:'1px solid #DDE3EA', background:'#fff', borderRadius:8, padding:'8px 10px 8px 32px', minHeight:36, fontSize:13, color:'#0F172A', outline:'none' }}
                />
              </div>
              <select value={stockFilter} onChange={e => setStockFilter(e.target.value)} aria-label="Filter by stock state" style={selectStyle}>
                <option value="all">All stock</option>
                <option value="in_stock">In stock</option>
                <option value="low">Low</option>
                <option value="out">Out</option>
              </select>
              <select value={sortValue} onChange={e => setSortValue(e.target.value)} aria-label="Sort products" style={selectStyle}>
                {PRODUCT_SORT_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </div>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:8, padding:'10px 12px', borderTop:'1px solid #E2E8F0', background:'#F8FAFC' }}>
              <div style={{ fontSize:12, color:'#475569', lineHeight:1.4, minWidth:0 }}>
                <strong style={{ fontWeight:700 }}>Stock and inventory APIs</strong> are available for products with stock tracking enabled.
                <div>Use <code>GET /api/products</code> or <code>GET /api/products/stock</code> to read catalog stock counts, then connect this feed to your webstore sync.</div>
              </div>
              <button type="button" onClick={() => setShowStockApiInfo(prev => !prev)} style={{ border:'1px solid #D1D5DB', background:'#fff', color:'#374151', borderRadius:8, padding:'8px 12px', fontSize:12, fontWeight:700, cursor:'pointer' }}>
                {showStockApiInfo ? 'Hide details' : 'Show API access'}
              </button>
            </div>
            {showStockApiInfo && (
              <div style={{ padding:'12px 14px', borderTop:'1px solid #E2E8F0', background:'#EFF6FF', color:'#0F172A', fontSize:12, lineHeight:1.6 }}>
                <div style={{ marginBottom:8 }}><strong>Available endpoints</strong></div>
                <div><code>GET /api/products</code> returns products with stock fields including <code>track_stock</code>, <code>stock_qty</code>, <code>low_stock_threshold</code>, and <code>remaining_stock</code>.</div>
                <div><code>GET /api/products/stock</code> returns the same catalog stock data in a smaller response.</div>
                <div style={{ marginTop:8 }}>Connect these APIs to your store sync by reading the product SKU and stock quantities, then update your storefront inventory on each sync cycle.</div>
              </div>
            )}
          </div>
        </section>

        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(210px, 1fr))', gap:10 }}>
          {kpiCards.map(card => <HomeKpiCard key={card.label} card={card}/>)}
        </div>

        <section className="dash-panel workspace-panel-flat" style={{ overflow:'hidden' }}>
          <div style={{ padding:'10px 12px', borderBottom:'1px solid #F1F5F9', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap', background:'#fff' }}>
            <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap', minWidth:0 }}>
              {hasInactive && (
                <label style={{ display:'inline-flex', alignItems:'center', gap:7, fontSize:12.5, color:'#475569', fontWeight:800, cursor:'pointer', whiteSpace:'nowrap' }}>
                  <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} style={{ width:15, height:15 }}/>
                  Show inactive products
                  <span style={{ color:'#94A3B8', fontWeight:800 }}>({productCountText(inactiveCount)})</span>
                </label>
              )}
              {!initialLoading && products.length > 0 && (
                <span style={{ fontSize:12.5, color:'#94A3B8', fontWeight:750, whiteSpace:'nowrap' }}>
                  {productCountText(sortedProducts.length)} shown
                </span>
              )}
            </div>
            {filtersActive && products.length > 0 && (
              <button onClick={clearFilters} style={{ border:'1px solid #E2E8F0', background:'#F8FAFC', color:'#475569', borderRadius:8, padding:'6px 8px', fontSize:12, fontWeight:850, cursor:'pointer' }}>Clear filters</button>
            )}
          </div>

          <div style={{ overflowX:'auto' }}>
            {initialLoading ? (
              <TableSkeleton/>
            ) : products.length === 0 ? (
              <EmptyState/>
            ) : sortedProducts.length === 0 ? (
              <NoResultsState/>
            ) : (
              <table style={{ width:'100%', minWidth:980, borderCollapse:'collapse', fontSize:13 }}>
                <thead>
                  <tr style={{ background:'#F8FAFC', borderBottom:'1px solid #E5E7EB' }}>
                    <th style={{ padding:'9px 12px', width:'32%', textAlign:'left' }}><SortHeader column="product" label="Product"/></th>
                    <th style={{ padding:'9px 12px', width:82, textAlign:'right' }}><SortHeader column="orders" label="Orders" align="right"/></th>
                    <th style={{ padding:'9px 12px', width:132, textAlign:'left' }}><SortHeader column="revenue" label="Revenue"/></th>
                    <th style={{ padding:'9px 12px', width:118, textAlign:'left' }}><SortHeader column="last_order" label="Last order"/></th>
                    <th style={{ padding:'9px 12px', width:158, textAlign:'left' }}><SortHeader column="stock" label="Stock"/></th>
                    <th style={{ padding:'9px 12px', width:92, textAlign:'right', fontSize:11, fontWeight:850, color:'#94A3B8', letterSpacing:'0.04em', textTransform:'uppercase' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedProducts.map((product, index) => {
                    const inactive = productIsInactive(product);
                    const issueReasons = productIssueReasons(product);
                    const hovered = hoveredProductId === product.id;
                    return (
                      <tr
                        key={product.id}
                        tabIndex={0}
                        onClick={() => setModalProduct(product)}
                        onKeyDown={event => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setModalProduct(product);
                          }
                        }}
                        onMouseEnter={() => setHoveredProductId(product.id)}
                        onMouseLeave={() => setHoveredProductId(null)}
                        onFocusCapture={() => setHoveredProductId(product.id)}
                        onBlurCapture={event => {
                          if (!event.currentTarget.contains(event.relatedTarget)) setHoveredProductId(null);
                        }}
                        style={{
                          borderBottom:'1px solid #F1F5F9',
                          background:hovered ? '#F8FBFF' : index % 2 ? '#FAFAFA' : '#fff',
                          cursor:'pointer',
                          opacity:inactive ? 0.62 : 1,
                          transition:'background 100ms ease, opacity 100ms ease',
                          outline:'none',
                        }}
                      >
                        <td style={{ padding:'5px 12px' }}>
                          <div style={{ display:'flex', alignItems:'center', gap:9, minWidth:0 }}>
                            <ProductThumb src={product.image_data} size={30}/>
                            <div style={{ minWidth:0 }}>
                              <div style={{ display:'flex', alignItems:'center', gap:7, minWidth:0 }}>
                                <span style={{ fontWeight:850, color:inactive ? '#64748B' : '#0F172A', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{product.name}</span>
                                {issueReasons.length > 0 && (
                                  <AlertTriangle size={13} strokeWidth={2.3} color="#D97706" style={{ flexShrink:0 }} title={issueReasons.join(', ')}/>
                                )}
                              </div>
                              <div style={{ marginTop:2, display:'flex', alignItems:'center', gap:8, fontSize:11.5, color:'#94A3B8', fontWeight:750, minWidth:0 }}>
                                <span style={{ whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{product.sku || 'No SKU'}</span>
                                <button
                                  onClick={event => { event.stopPropagation(); setAliasProduct(product); }}
                                  style={{ border:'none', background:'transparent', padding:0, color:'#64748B', fontSize:11.5, fontWeight:850, cursor:'pointer', whiteSpace:'nowrap' }}
                                >
                                  Aliases {product.aliases?.length || 0}
                                </button>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding:'5px 12px', textAlign:'right', fontWeight:850, color:inactive ? '#64748B' : '#0F172A', fontVariantNumeric:'tabular-nums' }}>{productCountText(product.order_count)}</td>
                        <td style={{ padding:'5px 12px' }}>{revenueCell(product)}</td>
                        <td style={{ padding:'5px 12px' }}>{lastOrderCell(product)}</td>
                        <td style={{ padding:'5px 12px' }}>{stockCell(product)}</td>
                        <td style={{ padding:'5px 12px', textAlign:'right', whiteSpace:'nowrap' }}>
                          <span style={{ display:'inline-flex', alignItems:'center', gap:6, opacity:hovered ? 1 : 0, pointerEvents:hovered ? 'auto' : 'none', transition:'opacity 100ms ease' }}>
                            <button onClick={event => { event.stopPropagation(); setModalProduct(product); }} title="Edit product" style={{ background:'#fff', border:'1px solid #E2E8F0', color:'#475569', borderRadius:7, padding:6, cursor:'pointer', display:'inline-flex' }}><Edit3 size={13} strokeWidth={2}/></button>
                            <button onClick={event => { event.stopPropagation(); remove(product); }} title="Delete product" style={{ background:'#fff', border:'1px solid #FECACA', color:'#DC2626', borderRadius:7, padding:6, cursor:'pointer', display:'inline-flex' }}><Trash2 size={13} strokeWidth={2}/></button>
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ background:'#F8FAFC', borderTop:'1px solid #CBD5E1' }}>
                    <td style={{ padding:'9px 12px', fontWeight:900, color:'#0F172A' }}>Totals</td>
                    <td style={{ padding:'9px 12px', textAlign:'right', fontWeight:900, color:'#0F172A', fontVariantNumeric:'tabular-nums' }}>{productCountText(filteredOrderTotal)}</td>
                    <td style={{ padding:'9px 12px', fontWeight:900, color:'#0F172A', fontVariantNumeric:'tabular-nums' }}>
                      {revenueLoading ? <SkeletonPill width={72} height={13}/> : formatMoneyCompact(filteredPrimaryRevenue.revenue, revenueCurrency)}
                      <span style={{ marginLeft:5 }}><CurrencyBreakdownBadge rows={filteredRevenueRows}/></span>
                    </td>
                    <td colSpan="3" style={{ padding:'9px 12px', color:'#94A3B8', fontSize:12, fontWeight:750 }}>
                      {productCountText(sortedProducts.length)} product{sortedProducts.length === 1 ? '' : 's'} in this view
                    </td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>

          {!initialLoading && sortedProducts.length > PRODUCTS_PAGE_SIZE && (
            <div style={{ borderTop:'1px solid #F1F5F9', padding:'10px 12px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap', background:'#fff' }}>
              <div style={{ fontSize:12.5, color:'#64748B', fontWeight:750 }}>
                Showing {productCountText(pageStart + 1)}-{productCountText(Math.min(pageStart + PRODUCTS_PAGE_SIZE, sortedProducts.length))} of {productCountText(sortedProducts.length)}
              </div>
              <div style={{ display:'flex', gap:7 }}>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1} style={{ border:'1px solid #D1D5DB', background:'#fff', color:currentPage <= 1 ? '#CBD5E1' : '#374151', borderRadius:8, padding:'7px 10px', fontSize:12.5, fontWeight:850, cursor:currentPage <= 1 ? 'not-allowed' : 'pointer' }}>Previous</button>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages} style={{ border:'1px solid #D1D5DB', background:'#fff', color:currentPage >= totalPages ? '#CBD5E1' : '#374151', borderRadius:8, padding:'7px 10px', fontSize:12.5, fontWeight:850, cursor:currentPage >= totalPages ? 'not-allowed' : 'pointer' }}>Next</button>
              </div>
            </div>
          )}
        </section>
      </div>

      {modalProduct !== undefined && (
        <ProductModal
          product={modalProduct}
          onClose={() => setModalProduct(undefined)}
          onSaved={() => { setModalProduct(undefined); onProductsChanged?.(); }}
        />
      )}
      {aliasProduct && <ProductAliasesModal product={aliasProduct} onClose={() => setAliasProduct(null)} onChanged={onProductsChanged}/>}
    </div>
  );
};
